import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase, useAuth } from "@/auth/AuthContext";
import AppHeader from "@/components/app/AppHeader";

const qrFor = (url) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=512x512&margin=12&data=${encodeURIComponent(url)}`;


const fmtDate = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return s; }
};

export default function EventDetail() {
  const { weddingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [wedding, setWedding] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [hidingId, setHidingId] = useState(null);
  const [reelStatus, setReelStatus] = useState(null);
  const [reelUrl, setReelUrl] = useState(null);
  const [generatingReel, setGeneratingReel] = useState(false);
  const [selectedMusic, setSelectedMusic] = useState("romantico");
  const [showMusicModal, setShowMusicModal] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [galleryPublic, setGalleryPublic] = useState(false);
  const [togglingGallery, setTogglingGallery] = useState(false);
  const touchStartX = useRef(null);
  const [customMusicUrl, setCustomMusicUrl] = useState(null);
  const [customMusicName, setCustomMusicName] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const reelIntervalRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", wedding_date: "", location: "" });
  const [saving, setSaving] = useState(false);
  const lightboxPhotos = uploads.filter((u) => !u.is_dedication_only);

  useEffect(() => () => { if (reelIntervalRef.current) clearInterval(reelIntervalRef.current); }, []);

  useEffect(() => {
    const n = lightboxPhotos.length;
    const handler = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") setLightboxIndex(i => i !== null ? (i - 1 + n) % n : null);
      if (e.key === "ArrowRight") setLightboxIndex(i => i !== null ? (i + 1) % n : null);
    };
    if (lightboxIndex !== null) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, lightboxPhotos.length]);

  // Auto-poll reel status on page load if already processing
  useEffect(() => {
    if (reelStatus !== "processing" || reelIntervalRef.current) return;
    reelIntervalRef.current = setInterval(async () => {
      const { data: statusData } = await supabase.functions.invoke("reel-status", {
        body: { wedding_id: weddingId },
      });
      if (statusData?.status === "ready") {
        setReelStatus("ready");
        setReelUrl(statusData.mp4_url);
        toast.success("Il tuo Wedding Reel è pronto!");
        clearInterval(reelIntervalRef.current);
        reelIntervalRef.current = null;
      } else if (statusData?.status === "failed") {
        setReelStatus("failed");
        toast.error("Generazione reel fallita.");
        clearInterval(reelIntervalRef.current);
        reelIntervalRef.current = null;
      }
    }, 5000);
  }, [reelStatus, weddingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const plan = user?.plan || "free";
  const effectivePlan = wedding?.plan_override || plan;
  const canDownloadZip = effectivePlan === "smart" || effectivePlan === "premium";

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) throw new Error("Non autenticato");

      const [{ data: wed, error: wErr }, { data: ups, error: uErr }] = await Promise.all([
        supabase.from("weddings").select("*").eq("id", weddingId).eq("owner_id", authUser.id).single(),
        supabase.from("uploads").select("*").eq("wedding_id", weddingId).order("created_at", { ascending: false }),
      ]);
      if (wErr) throw wErr;
      if (uErr) throw uErr;
      setWedding(wed);
      setGalleryPublic(wed?.gallery_public ?? false);
      setReelStatus(wed?.reel?.status ?? null);
      setReelUrl(wed?.reel?.mp4_url ?? null);
      setUploads(ups || []);
    } catch (e) {
      toast.error("Errore nel caricamento");
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [weddingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const guestUrl = useMemo(() => {
    if (!wedding) return "";
    return `${window.location.origin}/wedding/${wedding.slug}`;
  }, [wedding]);

  const galleryUrl = useMemo(() => {
    if (!wedding) return "";
    return `${window.location.origin}/gallery/${wedding.slug}`;
  }, [wedding]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(guestUrl);
      toast.success("Link copiato");
    } catch { toast.error("Impossibile copiare"); }
  };

  const toggleGallery = async () => {
    if (effectivePlan !== "smart" && effectivePlan !== "premium") return;
    setTogglingGallery(true);
    const newVal = !galleryPublic;
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) { toast.error("Sessione scaduta, ricarica la pagina."); return; }
      const { error } = await supabase
        .from("weddings")
        .update({ gallery_public: newVal })
        .eq("id", weddingId)
        .eq("owner_id", authUser?.id);
      if (error) throw error;
      setGalleryPublic(newVal);
      toast.success(newVal ? "Galleria pubblica attivata" : "Galleria pubblica disattivata");
    } catch {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setTogglingGallery(false);
    }
  };

  const copyGalleryLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: wedding?.title, url: galleryUrl });
      } else {
        await navigator.clipboard.writeText(galleryUrl);
        toast.success("Link galleria copiato");
      }
    } catch {
      toast.error("Impossibile condividere");
    }
  };

  const deleteWedding = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("weddings")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", weddingId)
        .eq("owner_id", authUser?.id);
      if (error) throw error;
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const deleteUpload = async (uploadId) => {
    setDeletingId(uploadId);
    try {
      const upload = uploads.find(u => u.id === uploadId);
      if (upload?.storage_path) {
        await supabase.storage.from("wedding-media").remove([upload.storage_path]);
      }
      // Scopiamo al wedding_id verificato in load() per difesa in profondità
      const { error } = await supabase.from("uploads").delete().eq("id", uploadId).eq("wedding_id", weddingId);
      if (error) throw error;
      setUploads(prev => prev.filter(u => u.id !== uploadId));
    } catch {
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeletingId(null);
    }
  };

  const toggleHidden = async (uploadId, currentHidden) => {
    setHidingId(uploadId);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) { toast.error("Sessione scaduta, ricarica la pagina."); setHidingId(null); return; }
      const { error } = await supabase
        .from("uploads")
        .update({ hidden: !currentHidden })
        .eq("id", uploadId)
        .eq("wedding_id", weddingId);
      if (error) throw error;
      setUploads((prev) =>
        prev.map((u) => u.id === uploadId ? { ...u, hidden: !currentHidden } : u)
      );
    } catch {
      setUploads((prev) =>
        prev.map((u) => u.id === uploadId ? { ...u, hidden: currentHidden } : u)
      );
      toast.error("Errore nell'aggiornamento");
    } finally {
      setHidingId(null);
    }
  };

  const downloadZip = async () => {
    if (!canDownloadZip) { toast.error("Disponibile per Smart e Premium"); return; }
    const toastId = toast.loading("Preparazione ZIP…");
    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      const extFromType = (mimeType) => {
        const map = { "jpeg": "jpg", "quicktime": "mov", "x-msvideo": "avi" };
        const raw = mimeType?.split("/")[1] || "bin";
        return map[raw] || raw;
      };
      // Sequential fetch to avoid memory spikes with large video files
      for (let i = 0; i < uploads.length; i++) {
        const u = uploads[i];
        try {
          const res = await fetch(u.public_url);
          const blob = await res.blob();
          const ext = extFromType(blob.type);
          const name = u.guest_label ? `${u.guest_label}.${ext}` : `${u.resource_type === "video" ? "video" : "foto"}-${i + 1}.${ext}`;
          zip.file(name, blob);
        } catch {
          // skip individual files that fail, don't abort the whole ZIP
        }
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${wedding?.title || "matrimonio"}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("ZIP scaricato!", { id: toastId });
    } catch {
      toast.error("Errore nel download ZIP.", { id: toastId });
    }
  };

  const handleMusicUpload = async (file) => {
    if (!file) return;
    setUploadingMusic(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${weddingId}/music/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("wedding-media").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from("wedding-media").getPublicUrl(path);
      setCustomMusicUrl(publicUrl);
      setCustomMusicName(file.name);
      setSelectedMusic(null);
    } catch {
      toast.error("Errore nel caricamento del brano.");
    } finally {
      setUploadingMusic(false);
    }
  };

  const startEdit = () => {
    setEditForm({
      title: wedding.title || "",
      wedding_date: wedding.wedding_date || "",
      location: wedding.location || "",
    });
    setEditing(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const t = editForm.title.trim();
    if (!t || t.length < 2) { toast.error("Titolo troppo corto"); return; }
    setSaving(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("weddings")
        .update({
          title: t,
          wedding_date: editForm.wedding_date || null,
          location: editForm.location.trim() || null,
        })
        .eq("id", weddingId)
        .eq("owner_id", authUser.id);
      if (error) throw error;
      setWedding((w) => ({
        ...w,
        title: t,
        wedding_date: editForm.wedding_date || null,
        location: editForm.location.trim() || null,
      }));
      toast.success("Salvato");
      setEditing(false);
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const REEL_LIMIT = 3;

  const generateReel = async () => {
    if (effectivePlan !== "premium") {
      toast.error("Il Wedding Reel è disponibile solo per il piano Premium.");
      return;
    }
    const reelCount = wedding?.reel_count ?? 0;
    if (reelCount >= REEL_LIMIT) {
      toast.error(`Hai raggiunto il limite di ${REEL_LIMIT} generazioni per questo matrimonio.`);
      return;
    }
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (!authUser) { toast.error("Sessione scaduta, ricarica la pagina."); return; }
    setGeneratingReel(true);
    try {
      const body = customMusicUrl
        ? { wedding_id: weddingId, music_url: customMusicUrl }
        : { wedding_id: weddingId, music: selectedMusic || "romantico" };

      const { error } = await supabase.functions.invoke("generate-reel", { body });
      if (error) throw error;

      await supabase.from("weddings").update({ reel_count: reelCount + 1 }).eq("id", weddingId).eq("owner_id", authUser.id);
      setWedding((w) => ({ ...w, reel_count: reelCount + 1 }));

      toast.success("Reel in generazione!");
      setReelStatus("processing");

      reelIntervalRef.current = setInterval(async () => {
        const { data: statusData } = await supabase.functions.invoke("reel-status", {
          body: { wedding_id: weddingId },
        });
        if (statusData?.status === "ready") {
          setReelStatus("ready");
          setReelUrl(statusData.mp4_url);
          toast.success("Il tuo Wedding Reel è pronto!");
          clearInterval(reelIntervalRef.current);
          reelIntervalRef.current = null;
        } else if (statusData?.status === "failed") {
          toast.error("Errore nella generazione del Reel.");
          setReelStatus("failed");
          clearInterval(reelIntervalRef.current);
          reelIntervalRef.current = null;
        }
      }, 5000);
    } catch (e) {
      console.error("Reel error:", e);
      let msg = e?.message || String(e);
      try {
        if (e?.context) {
          const body = await e.context.text();
          if (body) msg = body;
        }
      } catch {}
      toast.error(`Errore: ${msg}`);
    } finally {
      setGeneratingReel(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <AppHeader />
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#c9a86a] animate-spin" />
        </div>
      </div>
    );
  }

  if (!wedding) return null;

  const images = uploads.filter((u) => u.resource_type === "image" && !u.is_dedication_only);
  const videos = uploads.filter((u) => u.resource_type === "video" && !u.is_dedication_only);
  const dediche = uploads.filter((u) => u.dedication);
  const lightboxItem = lightboxIndex !== null ? lightboxPhotos[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-white" data-testid="event-detail-root">
      <AppHeader />

      <main className="max-w-[1240px] mx-auto px-4 md:px-8 py-6 md:py-16">
        <button type="button" onClick={() => navigate("/dashboard")} className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 hover:text-neutral-900 flex items-center gap-1">
          ← Dashboard
        </button>

        {/* Header info */}
        <div className="mt-5 md:mt-6">
          {editing ? (
            <form onSubmit={saveEdit} className="flex flex-col gap-3 max-w-xl">
              <input
                type="text"
                required
                minLength={2}
                maxLength={120}
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Titolo / Nomi degli sposi"
                className="bg-white border border-neutral-200 rounded-[10px] px-4 py-3 text-[22px] md:text-[32px] font-display font-light tracking-tight outline-none focus:border-[color:var(--wf-gold)] w-full"
                autoFocus
              />
              <div className="flex gap-3">
                <input
                  type="date"
                  value={editForm.wedding_date}
                  onChange={(e) => setEditForm((f) => ({ ...f, wedding_date: e.target.value }))}
                  className="bg-white border border-neutral-200 rounded-[10px] px-4 py-2.5 text-[14px] outline-none focus:border-[color:var(--wf-gold)]"
                />
                <input
                  type="text"
                  maxLength={200}
                  value={editForm.location}
                  onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="Luogo (facoltativo)"
                  className="flex-1 bg-white border border-neutral-200 rounded-[10px] px-4 py-2.5 text-[14px] outline-none focus:border-[color:var(--wf-gold)]"
                />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <button type="submit" disabled={saving}
                  className="wf-btn-primary rounded-full px-5 py-2 text-[12px] tracking-wide disabled:opacity-50">
                  {saving ? "Salvataggio…" : "Salva"}
                </button>
                <button type="button" onClick={() => setEditing(false)} disabled={saving}
                  className="border border-neutral-200 rounded-full px-5 py-2 text-[12px] tracking-wide hover:bg-neutral-50 transition">
                  Annulla
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center gap-3">
                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-400">
                  {fmtDate(wedding.wedding_date) || "Data da definire"}
                  {wedding.location && ` · ${wedding.location}`}
                </div>
                <button
                  type="button"
                  onClick={startEdit}
                  className="text-[10px] tracking-[0.12em] uppercase text-neutral-400 hover:text-neutral-700 border border-neutral-200 rounded-full px-2.5 py-1 transition"
                >
                  Modifica
                </button>
              </div>
              <h1 className="mt-2 font-display text-[32px] md:text-[60px] tracking-[-0.02em] font-light text-neutral-950 leading-[1.05]">
                {wedding.title}
              </h1>
              {wedding.welcome_message && (
                <p className="mt-3 text-[14px] md:text-[17px] text-neutral-500 leading-relaxed max-w-2xl">{wedding.welcome_message}</p>
              )}
            </>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-3 gap-3 max-w-sm">
          {[
            { label: "Ricordi", value: uploads.length },
            { label: "Foto", value: images.length },
            { label: "Video", value: videos.length },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white border border-neutral-200 rounded-2xl p-4 flex flex-col gap-1">
              <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-400">{label}</div>
              <div className="font-display text-[28px] md:text-[32px] font-light tracking-tight text-neutral-950">{value}</div>
            </div>
          ))}
        </div>

        {/* QR + Link — stack su mobile */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
          {/* Link ospiti */}
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-3">Link per gli ospiti</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                readOnly
                value={guestUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 text-[13px] outline-none text-neutral-700 truncate"
              />
              <div className="flex gap-2">
                <button type="button" onClick={copyLink} className="flex-1 sm:flex-none wf-btn-primary rounded-2xl px-4 py-3 text-[12px] tracking-wide whitespace-nowrap">
                  Copia
                </button>
                <a href={guestUrl} target="_blank" rel="noopener noreferrer" className="flex-1 sm:flex-none text-center border border-neutral-200 rounded-2xl px-4 py-3 text-[12px] tracking-wide text-neutral-600 hover:border-neutral-400 transition whitespace-nowrap">
                  Anteprima
                </a>
              </div>
            </div>
          </div>

          {/* QR */}
          <div className="md:col-span-5">
            <div className="border border-neutral-200 rounded-2xl p-5 flex flex-col items-center gap-4">
              <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-400">QR code</div>
              <img src={qrFor(guestUrl)} alt="QR code" className="w-36 h-36 md:w-48 md:h-48 object-contain" />
              <a
                href={qrFor(guestUrl)}
                download={`wedflow-${wedding.slug}.png`}
                className="w-full text-center border border-neutral-200 rounded-full py-2.5 text-[12px] tracking-wide text-neutral-600 hover:border-neutral-400 transition"
              >
                Scarica QR
              </a>
            </div>
          </div>
        </div>

        {/* Galleria pubblica condivisibile — solo Smart e Premium */}
        {(effectivePlan === "smart" || effectivePlan === "premium") && (
          <div className="mt-8 rounded-[14px] border border-neutral-200 p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-neutral-500 mb-1">
                  Galleria condivisibile
                </div>
                <p className="text-[13px] text-neutral-600 max-w-lg">
                  Condividi le foto con tutti gli ospiti in una galleria pubblica elegante.
                  {effectivePlan === "premium" && " Nessun branding WedFlow."}
                </p>
              </div>
              <button
                type="button"
                onClick={toggleGallery}
                disabled={togglingGallery}
                aria-pressed={galleryPublic}
                role="switch"
                className="relative inline-flex flex-none h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: galleryPublic ? "#c9a86a" : "#e5e5e5" }}
              >
                <span
                  className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  style={{ transform: galleryPublic ? "translateX(20px)" : "translateX(0px)" }}
                />
              </button>
            </div>

            {galleryPublic && (
              <div className="mt-4 flex flex-col sm:flex-row gap-2">
                <input
                  readOnly
                  value={galleryUrl}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 text-[13px] outline-none text-neutral-700 truncate"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={copyGalleryLink}
                    className="flex-1 sm:flex-none wf-btn-primary rounded-2xl px-4 py-3 text-[12px] tracking-wide whitespace-nowrap"
                  >
                    Condividi
                  </button>
                  <a
                    href={galleryUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 sm:flex-none text-center border border-neutral-200 rounded-2xl px-4 py-3 text-[12px] tracking-wide text-neutral-600 hover:border-neutral-400 transition whitespace-nowrap"
                  >
                    Anteprima
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Galleria */}
        <section className="mt-12 md:mt-16">
          {/* Toolbar galleria */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <h2 className="font-display text-[24px] md:text-[34px] tracking-[-0.02em] font-light text-neutral-950">Galleria</h2>
            <div className="flex flex-wrap items-center gap-2">
              {effectivePlan === "premium" && (
                <div className="flex items-center gap-2">
                  {(() => {
                    const reelCount = wedding?.reel_count ?? 0;
                    const exhausted = reelCount >= REEL_LIMIT;
                    return (
                      <button
                        type="button"
                        onClick={() => (!exhausted && reelStatus !== "processing") ? setShowMusicModal(true) : null}
                        disabled={generatingReel || reelStatus === "processing" || exhausted}
                        className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border transition-all disabled:opacity-40"
                        style={{ borderColor: "rgba(124,92,191,0.5)", color: "#7c5cbf" }}
                        title={exhausted ? `Limite di ${REEL_LIMIT} generazioni raggiunto` : reelCount > 0 ? `${REEL_LIMIT - reelCount} generazioni rimanenti` : undefined}
                      >
                        {reelStatus === "processing" ? "⏳ Generazione..." : "🎬 Reel"}
                        {!exhausted && reelCount > 0 && reelStatus !== "processing" && (
                          <span className="text-[9px] opacity-50 normal-case tracking-normal">({REEL_LIMIT - reelCount} rimaste)</span>
                        )}
                        {exhausted && (
                          <span className="text-[9px] opacity-60 normal-case tracking-normal">(esaurite)</span>
                        )}
                      </button>
                    );
                  })()}
                  {reelUrl && (
                    <a
                      href={reelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border transition-all"
                      style={{ borderColor: "rgba(124,92,191,0.5)", color: "#7c5cbf" }}
                    >
                      ▶ Guarda il Reel
                    </a>
                  )}
                </div>
              )}
              {/* Live button */}
              {wedding?.slug && (
                <>
                  <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
                  {effectivePlan === "premium" ? (
                    <button
                      type="button"
                      onClick={() => { window.open(`/live/${wedding.slug}`, "_blank"); }}
                      className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border transition-all"
                      style={{ borderColor: "rgba(201,168,106,0.5)", color: "#9b7d40" }}
                    >
                      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "red", flexShrink: 0, animation: "pulse 1.5s infinite" }} />
                      Live
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled
                      className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border transition-all"
                      style={{ borderColor: "#e5e5e5", color: "#bbb" }}
                    >
                      <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#ccc", flexShrink: 0 }} />
                      Live
                    </button>
                  )}
                </>
              )}
              <button
                type="button"
                onClick={downloadZip}
                className="flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border transition-all"
                style={canDownloadZip
                  ? { borderColor: "rgba(201,168,106,0.5)", color: "#9b7d40" }
                  : { borderColor: "#e5e5e5", color: "#bbb" }
                }
              >
                ⬇ ZIP {!canDownloadZip && "🔒"}
              </button>
              <button type="button" onClick={load} className="text-[11px] tracking-[0.12em] uppercase px-3 py-2 rounded-full border border-neutral-200 text-neutral-500 hover:text-neutral-900 transition">
                Aggiorna
              </button>
            </div>
          </div>

          {uploads.length === 0 ? (
            <div className="border border-dashed border-neutral-200 rounded-2xl p-10 md:p-16 text-center text-neutral-400 text-[14px]">
              Ancora nessun ricordo. Condividi il QR code con i tuoi ospiti.
            </div>
          ) : (
            <div style={{ columns: "2", columnGap: "8px" }} className="md:columns-3 lg:columns-4">
              {lightboxPhotos.map((u) => (
                <div
                  key={u.id}
                  className="relative mb-2 overflow-hidden rounded-xl border border-neutral-100 bg-neutral-100 group break-inside-avoid cursor-pointer"
                  onClick={() => setLightboxIndex(lightboxPhotos.findIndex(p => p.id === u.id))}
                >
                  {u.resource_type === "video" ? (
                    <video
                      src={u.public_url + "#t=0.1"}
                      className="w-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                    />
                  ) : (
                    <img src={u.public_url} alt={u.guest_label || "Ricordo"} className="w-full object-cover" loading="lazy" />
                  )}

                  {u.hidden && (
                    <div
                      className="absolute inset-0 bg-black/25 flex items-center justify-center pointer-events-none"
                      style={{ zIndex: 2 }}
                    >
                      <span className="text-[10px] tracking-wide uppercase text-white bg-black/50 px-2 py-1 rounded-full">
                        Nascosta
                      </span>
                    </div>
                  )}

                  {/* Overlay mobile-friendly — sempre visibile su mobile */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 md:transition-opacity flex flex-col justify-between p-2.5">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteUpload(u.id); }}
                        disabled={deletingId === u.id}
                        className="hidden md:flex w-7 h-7 rounded-full items-center justify-center text-white text-[11px]"
                        style={{ background: "rgba(220,38,38,0.8)" }}
                      >
                        {deletingId === u.id ? "…" : "✕"}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleHidden(u.id, u.hidden ?? false); }}
                        disabled={hidingId === u.id}
                        className="hidden md:flex w-7 h-7 rounded-full items-center justify-center text-white text-[10px] ml-1"
                        style={{ background: (u.hidden) ? "rgba(100,100,100,0.8)" : "rgba(0,0,0,0.55)" }}
                        title={(u.hidden) ? "Rendi visibile in galleria" : "Nascondi dalla galleria pubblica"}
                      >
                        {hidingId === u.id ? "…" : (u.hidden ? "👁" : "🚫")}
                      </button>
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1 flex-wrap">
                        {u.guest_label && (
                          <span className="text-[10px] text-white/90 truncate max-w-[70%]">{u.guest_label}</span>
                        )}
                        {u.resource_type === "video" && (
                          <span className="text-[9px] tracking-wide uppercase px-1.5 py-0.5 rounded-full bg-black/60 text-white ml-auto">Video</span>
                        )}
                      </div>
                      {u.dedication && (
                        <p className="text-[11px] italic text-white/80 leading-snug line-clamp-3">"{u.dedication}"</p>
                      )}
                    </div>
                  </div>

                  {/* Bottone elimina sempre visibile su mobile */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); deleteUpload(u.id); }}
                    disabled={deletingId === u.id}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] md:hidden"
                    style={{ background: "rgba(220,38,38,0.7)" }}
                  >
                    {deletingId === u.id ? "…" : "✕"}
                  </button>

                  {/* Bottone nascondi sempre visibile su mobile */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggleHidden(u.id, u.hidden ?? false); }}
                    disabled={hidingId === u.id}
                    className="absolute top-9 right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] md:hidden"
                    style={{ background: (u.hidden) ? "rgba(100,100,100,0.7)" : "rgba(0,0,0,0.5)" }}
                  >
                    {hidingId === u.id ? "…" : (u.hidden ? "👁" : "🚫")}
                  </button>

                  {u.is_cover && (
                    <div className="absolute top-1.5 left-1.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: "#c9a86a", color: "#fff" }}>★</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Diario dei Ricordi — solo Premium */}
        {effectivePlan === "premium" && (
          <section className="mt-12 md:mt-16">
            <div className="mb-5">
              <h2 className="font-display text-[24px] md:text-[34px] tracking-[-0.02em] font-light text-neutral-950">
                Diario dei Ricordi
              </h2>
              <p className="text-[13px] text-neutral-400 mt-1">I messaggi privati dei tuoi ospiti</p>
            </div>

            {dediche.length === 0 ? (
              <div className="border border-dashed border-neutral-200 rounded-2xl p-10 text-center text-neutral-400 text-[14px]">
                Ancora nessun messaggio. Gli ospiti possono scrivere una dedica dalla pagina di caricamento.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {dediche
                  .slice()
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map((d) => (
                    <div
                      key={d.id}
                      className="flex items-start gap-4 border border-neutral-100 rounded-2xl p-4 bg-white"
                    >
                      {d.public_url && !d.is_dedication_only && (
                        <img
                          src={d.public_url}
                          alt=""
                          className="w-14 h-14 rounded-xl object-cover flex-none"
                          loading="lazy"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-[10px] tracking-[0.18em] uppercase text-neutral-400">
                            {d.guest_label || "Ospite anonimo"}
                          </span>
                          <span className="text-[10px] text-neutral-300 flex-none">
                            {fmtDate(d.created_at)}
                          </span>
                        </div>
                        <p className="text-[14px] font-light italic text-neutral-700 leading-relaxed">
                          "{d.dedication}"
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </section>
        )}

        {/* Danger zone */}
        <section className="mt-16 pt-8 border-t border-neutral-100">
          {!confirmDelete ? (
            <button type="button" onClick={() => setConfirmDelete(true)} className="text-[11px] tracking-[0.18em] uppercase text-neutral-400 hover:text-red-500 transition-colors">
              Elimina questo matrimonio
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              <span className="text-[14px] text-neutral-700">L'evento verrà spostato nel cestino. Potrai ripristinarlo entro 30 giorni, dopodiché sarà eliminato definitivamente.</span>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setConfirmDelete(false)} className="border border-neutral-200 rounded-full px-4 py-2 text-[12px] text-neutral-600">Annulla</button>
                <button type="button" onClick={deleteWedding} className="rounded-full px-4 py-2 text-[12px] bg-red-600 text-white">Sposta nel cestino</button>
              </div>
            </div>
          )}
        </section>
      </main>

      {/* Modal musica */}
      {showMusicModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="bg-white rounded-[20px] p-6 w-full max-w-sm shadow-2xl">
            <h3 className="font-display text-[20px] font-light text-neutral-950 mb-1">Scegli la musica</h3>
            <p className="text-[12px] text-neutral-400 mb-4">La colonna sonora del tuo Wedding Reel.</p>

            {/* Upload MP3 personalizzato */}
            <div
              className="mb-4 rounded-2xl border px-4 py-3 transition-all"
              style={{ borderColor: customMusicUrl ? "#c9a86a" : "#e5e5e5", background: customMusicUrl ? "#fdf8f0" : "#f9f9f9" }}
            >
              <p className="text-[12px] font-medium text-neutral-700 mb-2">🎶 Carica il tuo brano</p>
              {customMusicUrl ? (
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-neutral-500 truncate max-w-[160px]">{customMusicName}</span>
                  <button type="button" onClick={() => { setCustomMusicUrl(null); setCustomMusicName(null); setSelectedMusic("romantico"); }} className="text-[11px] text-red-400 ml-2">Rimuovi</button>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer">
                  <span className="text-[11px] px-3 py-1.5 rounded-full border border-neutral-300 text-neutral-600 hover:border-[#c9a86a] transition-colors">
                    {uploadingMusic ? "Caricamento…" : "Scegli file MP3"}
                  </span>
                  <span className="text-[10px] text-neutral-400">max 50MB</span>
                  <input
                    type="file"
                    accept="audio/mpeg,audio/mp3,.mp3"
                    className="hidden"
                    disabled={uploadingMusic}
                    onChange={(e) => handleMusicUpload(e.target.files?.[0])}
                  />
                </label>
              )}
            </div>

            <div className="flex items-center gap-2 mb-3">
              <div className="flex-1 h-px bg-neutral-100" />
              <span className="text-[10px] text-neutral-400 uppercase tracking-widest">oppure scegli</span>
              <div className="flex-1 h-px bg-neutral-100" />
            </div>

            <div className="space-y-2">
              {[
                { key: "romantico", label: "Romantico", desc: "Pianoforte delicato" },
                { key: "festoso", label: "Festoso", desc: "Jazz allegro" },
                { key: "cinematografico", label: "Cinematografico", desc: "Orchestra epica" },
                { key: "moderno", label: "Moderno", desc: "Pop strumentale" },
                { key: "classico", label: "Classico", desc: "Quartetto d'archi" },
              ].map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => { setSelectedMusic(m.key); setCustomMusicUrl(null); setCustomMusicName(null); }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl border transition-all text-left"
                  style={{
                    borderColor: !customMusicUrl && selectedMusic === m.key ? "#c9a86a" : "#e5e5e5",
                    background: !customMusicUrl && selectedMusic === m.key ? "#fdf8f0" : "#fff",
                  }}
                >
                  <div>
                    <p className="text-[13px] font-medium text-neutral-900">🎵 {m.label}</p>
                    <p className="text-[11px] text-neutral-400">{m.desc}</p>
                  </div>
                  {!customMusicUrl && selectedMusic === m.key && <span style={{ color: "#c9a86a" }}>✓</span>}
                </button>
              ))}
            </div>

            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => setShowMusicModal(false)} className="flex-1 py-3 rounded-full border border-neutral-200 text-[12px] text-neutral-600">Annulla</button>
              <button
                type="button"
                disabled={uploadingMusic}
                onClick={() => { setShowMusicModal(false); generateReel(); }}
                className="flex-1 py-3 rounded-full text-[13px] font-medium text-white disabled:opacity-50"
                style={{ background: "#c9a86a" }}
              >
                Genera Reel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxItem && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setLightboxIndex(null)}
          onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            const n = lightboxPhotos.length;
            if (Math.abs(delta) > 50) {
              setLightboxIndex(i => delta < 0 ? (i + 1) % n : (i - 1 + n) % n);
            }
            touchStartX.current = null;
          }}
        >
          {/* Chiudi */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            style={{ position: "absolute", top: 16, right: 16, width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
          >✕</button>

          {/* Freccia sinistra */}
          {lightboxPhotos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + lightboxPhotos.length) % lightboxPhotos.length); }}
              style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >‹</button>
          )}

          {/* Freccia destra */}
          {lightboxPhotos.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % lightboxPhotos.length); }}
              style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", fontSize: 24, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1 }}
            >›</button>
          )}

          {/* Media */}
          {lightboxItem.resource_type === "video" ? (
            <video
              key={lightboxItem.id}
              src={lightboxItem.public_url}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "85vw", maxHeight: "90vh", borderRadius: 12 }}
            />
          ) : (
            <img
              key={lightboxItem.id}
              src={lightboxItem.public_url}
              alt={lightboxItem.guest_label || ""}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: "85vw", maxHeight: "90vh", objectFit: "contain", borderRadius: 12 }}
            />
          )}

          {/* Contatore */}
          {lightboxPhotos.length > 1 && (
            <div style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", color: "rgba(255,255,255,0.6)", fontSize: 12, pointerEvents: "none" }}>
              {lightboxIndex + 1} / {lightboxPhotos.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
